import { atom } from 'jotai';
import type { Polygon } from 'geojson';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { defaultOriginTargetRule, type ColorRule } from './color-by';
import type { PropertyFilter } from './property-filters';
import type {
  ComparisonResponse,
  ComparisonErrorResponse,
  ComparisonRequestPayload,
  WellQueryResult,
  UnitVersionResponse,
  SubtractGeojsonResponse,
} from './types';

/**
 * Mutation atom for fetching comparison data via POST.
 * Accepts the request payload as an argument when triggered.
 * Returns the comparison response on success, or throws on error.
 *
 * Usage:
 *   const triggerComparison = useSetAtom(comparisonAtom);
 *   const [state] = useAtom(comparisonLoadableAtom);
 *   triggerComparison(payload);
 *
 * Loadable state shape:
 *   { state: 'hasData', data: null }              — before first trigger
 *   { state: 'loading' }                           — POST in flight
 *   { state: 'hasData', data: ComparisonResponse } — success
 *   { state: 'hasError', error: Error }            — failure
 */
const [comparisonAtom, comparisonLoadableAtom] = mutationAtomWithLoadable(
  async (
    _get,
    _set,
    payload: ComparisonRequestPayload
  ): Promise<ComparisonResponse> => {
    const abortController = new AbortController();
    const response = await request.post<
      ComparisonResponse,
      ComparisonErrorResponse
    >('/packages/comparisons', payload, abortController);

    if (response.type === 'success') {
      return response.data;
    }

    if (response.type === 'error') {
      const msg =
        response.data?.errors?.join(', ') ??
        response.data?.error ??
        'Comparison failed';
      throw new Error(msg);
    }

    throw new Error(response.message);
  }
);

export { comparisonAtom, comparisonLoadableAtom };

// ── Global Map Filter ──

/** Spatial filter mode for the comparison map (sidebar → map). */
export type MapSpatialFilter = 'none' | 'extent' | 'aoi';

/** Selected Global Map Filter mode; shared between the sidebar and the map. */
export const mapSpatialFilterAtom = atom<MapSpatialFilter>('none');

/**
 * The user-drawn AOI polygon for the "Draw AOI Polygon" mode. Shared between the
 * sidebar's applied-AOI card (Edit / Clear) and the map's `DrawAoiControl`. `null`
 * = nothing drawn yet. In-memory only.
 */
export const aoiPolygonAtom = atom<Polygon | null>(null);

/**
 * Whether the drawn AOI is in vertex-editing (`direct_select`) on the map. A
 * controlled, two-way mirror: the sidebar's applied-AOI "Edit" sets it `true`, and
 * `DrawAoiControl` writes it back (`onEditingChange`) from the map's actual mode —
 * so it also reflects native click-to-edit and clears when the user clicks away.
 * The AOI geometry stays the single source of truth in `aoiPolygonAtom`; this is
 * only the edit-mode flag. In-memory only.
 */
export const aoiEditingAtom = atom(false);

// ── Color by Attribute & Property Filters ──

/**
 * Ordered color rules (sidebar → map + property tables). Seeded with the default
 * Origin vs Target rule so the map's membership colors are rule-driven.
 */
export const colorRulesAtom = atom<ColorRule[]>([defaultOriginTargetRule()]);

/** Active property filters (sidebar → Properties list). */
export const propertyFiltersAtom = atom<PropertyFilter[]>([]);

// ── Zoom to property (Properties list → map) ──

/**
 * A request to fit the comparison map to a single property (slot). A Properties
 * card sets it on click; `ComparisonMap` reads it and flies to that slot's
 * geometry. `nonce` makes each click a fresh request, so re-selecting the same
 * property re-triggers the fit. `null` = nothing requested. In-memory only.
 */
export interface ZoomToPropertyRequest {
  slotId: string;
  nonce: number;
}

const zoomNonceAtom = atom(0);

/** The pending zoom-to-property request; read by the map. */
export const zoomToPropertyAtom = atom<ZoomToPropertyRequest | null>(null);

/**
 * Write-only action: ask the map to fit to a property by its uniqueSlotId.
 * Bumps an internal nonce so re-selecting the same property re-fires the fit.
 */
export const requestZoomToPropertyAtom = atom(
  null,
  (get, set, slotId: string) => {
    const nonce = get(zoomNonceAtom) + 1;
    set(zoomNonceAtom, nonce);
    set(zoomToPropertyAtom, { slotId, nonce });
  }
);

// ── Geometry cache atoms ──

const wellCacheAtom = atom<Record<string, WellQueryResult>>({});
const unitCacheAtom = atom<Record<string, UnitVersionResponse>>({});
const subtractsCacheAtom = atom<Record<string, SubtractGeojsonResponse>>({});

export { wellCacheAtom, unitCacheAtom, subtractsCacheAtom };

// ── Geometry fetch atoms ──

/**
 * Fetches well geometry via POST /queries/wells.
 * Accepts UWI as argument. Writes result to wellCacheAtom.
 */
const [fetchWellAtom, fetchWellLoadableAtom] = mutationAtomWithLoadable(
  async (_get, set, uwi: string): Promise<WellQueryResult | null> => {
    const abortController = new AbortController();
    const response = await request.post<
      { results: WellQueryResult[] },
      unknown
    >(
      '/queries/wells',
      { uwi, includeDeviationSurvey2d: true },
      abortController
    );
    if (response.type === 'success' && response.data.results.length > 0) {
      const result = response.data.results[0];
      set(wellCacheAtom, (prev) => ({ ...prev, [uwi]: result }));
      return result;
    }
    return null;
  }
);

/**
 * Fetches unit geometry via GET /development_areas/{name}/versions/current.
 * Accepts dev area name as argument. Writes result to unitCacheAtom.
 */
const [fetchUnitAtom, fetchUnitLoadableAtom] = mutationAtomWithLoadable(
  async (
    _get,
    set,
    devAreaName: string
  ): Promise<UnitVersionResponse | null> => {
    const abortController = new AbortController();
    const response = await request.get<UnitVersionResponse>(
      `/development_areas/${devAreaName}/versions/current`,
      abortController
    );
    if (response && typeof response === 'object' && !('error' in response)) {
      set(unitCacheAtom, (prev) => ({
        ...prev,
        [devAreaName]: response as UnitVersionResponse,
      }));
      return response as UnitVersionResponse;
    }
    return null;
  }
);

/**
 * Fetches subtract geometries via GET /development_areas/{name}/slot_allocations/geojsons.
 * Accepts dev area name as argument. Writes result to subtractsCacheAtom.
 */
const [fetchSubtractsAtom, fetchSubtractsLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      set,
      devAreaName: string
    ): Promise<SubtractGeojsonResponse | null> => {
      const abortController = new AbortController();
      const response = await request.get<SubtractGeojsonResponse>(
        `/development_areas/${devAreaName}/slot_allocations/geojsons?type=subtracts`,
        abortController
      );
      if (response && typeof response === 'object' && !('error' in response)) {
        set(subtractsCacheAtom, (prev) => ({
          ...prev,
          [devAreaName]: response as SubtractGeojsonResponse,
        }));
        return response as SubtractGeojsonResponse;
      }
      return null;
    }
  );

export {
  fetchWellAtom,
  fetchWellLoadableAtom,
  fetchUnitAtom,
  fetchUnitLoadableAtom,
  fetchSubtractsAtom,
  fetchSubtractsLoadableAtom,
};
